<template>
<div>
  <EntitiesTable
    :config="config"
    :search="search"
    :showDownloadCsvButton="false"
    :reLoadData="reLoadData"
    @dataLoaded="updateReloadData"
  >
    <template v-slot:top>
      <v-toolbar flat>
        <v-toolbar-title class="pr-2">Release Notes</v-toolbar-title>
        <v-btn v-if="isAdminContext" x-small color="secondary" @click="openNewReleaseNotesModal">
          Add
        </v-btn>
        <v-spacer/>
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Search"
          class="pr-4"
          single-line
          hide-details
          clearable
          clear-icon="$xmark"
        />
      </v-toolbar>
    </template>
    <template v-slot:item.releaseDate="{ item }">{{ item.releaseDate | dateTimeFormat }}</template>
    <template v-slot:item.actions="{ item }">
      <template v-if="isAdminContext">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon small class="mr-2" @click="viewRelease(item)" v-bind="attrs" v-on="on">
              mdi-magnify
            </v-icon>
          </template>
          <span>View Release Notes</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon small class="mr-2" @click="editRelease(item)" v-bind="attrs" v-on="on">
              mdi-pencil
            </v-icon>
          </template>
          <span>Edit Release Notes</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon small @click="promptForDelete(item)" v-bind="attrs" v-on="on">
              mdi-delete
            </v-icon>
          </template>
          <span>Delete Release Notes</span>
        </v-tooltip>
      </template>
      <v-btn v-else @click="viewRelease(item)" color="secondary" dense x-small>
        View
      </v-btn>
    </template>
  </EntitiesTable>
  <v-dialog v-model="showDeleteModal" persistent max-width="700">
    <v-card v-if="releaseNotesToDelete" justify="center" class="p-3">
      <v-card-title>
        Are you sure you want to delete the release?<br>{{ ` ${releaseNotesToDelete.releaseId}` }}
      </v-card-title>
      <v-card-actions>
        <v-spacer />
        <v-btn small color="secondary" @click="showDeleteModal = false">No</v-btn>
        <v-btn small color="secondary" @click="deleteReleaseNotes">Yes</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  <Modal ref="viewModal" style="width: 640px;">
    <template>
      <h3>Release {{ releaseNotesToEdit.releaseId }}</h3>
      <p>Date: {{ releaseNotesToEdit.releaseDate | dateTimeFormat }}</p>
      <FormTextarea
        v-model="releaseNotesToEdit.releaseNotes"
        readonly
        rows="13"
        name="releaseNotes"
        :textStyle="{
          backgroundColor: '#FFFFFF',
          height: 'auto',
          maxHeight: '35vh',
          minWidth: '600px',
          outline: '0.5px solid black',
        }"
      />
    </template>
  </Modal>
  <LoadingModal loadingText="Deleting..." :showModal="isDeleting" />
  <ReleaseNotesNew ref="newModal" @created="getData" />
  <ReleaseNotesEdit ref="editModal" @created="getData" :releaseNotesToEdit="releaseNotesToEdit" />
</div>
</template>

<script lang="ts">
import Vue from 'vue';
import { mapGetters } from 'vuex';
import EntitiesTable from '@/common/components/entities/EntitiesTable.vue';
import api from '@/common/api';
import FormTextarea from '@/common/components/forms/Textarea.vue';
import Modal from '@/common/components/Modal.vue';
import LoadingModal from '@/common/components/LoadingModal.vue';
import { ReleaseNotes } from '@/common/components/support/support.types';
import ReleaseNotesNew from '@/admin/views/release-notes/ReleaseNotesNew.vue';
import ReleaseNotesEdit from '@/admin/views/release-notes/ReleaseNotesEdit.vue';
import debounce from 'lodash/debounce';

export default Vue.extend({
  data() {
    return {
      search: '' as string,
      reLoadData: false,
      showDeleteModal: false as boolean,
      releaseNotesToDelete: null as ReleaseNotes | null,
      releaseNotesToEdit: {} as object,
      isDeleting: false as boolean,
      config: {
        pageTitle: 'Release Notes',
        header: 'Release Notes',
        routerBase: 'release-notes',
        apiPath: 'release-notes',
        // need to set reportFileName to trigger the underlying "dataLoaded" event
        reportFileName: 'entities_table_data_loaded',
        entityName: 'releaseNotes',
        identifierProperty: 'id',
        filters: [],
        headers: [
          {
            text: 'ID',
            value: 'id',
            align: ' d-none',
          },
          {
            text: 'Release',
            value: 'releaseId',
            sortable: true,
            class: 'entity-table-header subtitle-2 font-weight-bold black--text',
            width: '10rem',
          },
          {
            text: 'Date',
            value: 'releaseDate',
            sortable: true,
            class: 'entity-table-header subtitle-2 font-weight-bold black--text',
            width: '10rem',
          },
          {
            text: 'Actions',
            value: 'actions',
            sortable: false,
            align: 'center',
            class: 'entity-table-header subtitle-2 font-weight-bold black--text',
            width: '10rem',
          },
        ],
      },
    };
  },
  watch: {
    search: {
      handler: debounce(function (this: any) {
        this.getData();
      }, 1000),
      immediate: true,
    },
  },
  computed: {
    ...mapGetters(['isAdminContext']),
  },
  methods: {
    getData() {
      this.showDeleteModal = false;
      this.releaseNotesToDelete = null;
      this.releaseNotesToEdit = {};
      this.reLoadData = true;
    },
    openNewReleaseNotesModal() {
      (this.$refs.newModal as any).show();
    },
    viewRelease(releaseNotes: ReleaseNotes) {
      this.releaseNotesToEdit = releaseNotes;
      (this.$refs.viewModal as any).show();
    },
    editRelease(releaseNotes: ReleaseNotes) {
      this.releaseNotesToEdit = releaseNotes;
      (this.$refs.editModal as any).show();
    },
    promptForDelete(releaseNotes: ReleaseNotes) {
      this.showDeleteModal = true;
      this.releaseNotesToDelete = releaseNotes;
    },
    deleteReleaseNotes() {
      this.reLoadData = false;
      if (this.releaseNotesToDelete === null) {
        this.showDeleteModal = false;
        return;
      }
      this.isDeleting = true;
      api
        .delete(`release-notes/${this.releaseNotesToDelete.id}`)
        .then(() => {
          this.getData();
        })
        .catch(({ response }) => {
          this.showDeleteModal = false;
          this.releaseNotesToDelete = null;
          const message = response?.data?.message || 'Failed to delete. Try again.';
          this.$toasted.error(`${message} (${response.status})`);
        })
        .finally(() => {
          this.isDeleting = false;
          this.showDeleteModal = false;
          this.releaseNotesToDelete = null;
        });
    },
    updateReloadData() {
      this.reLoadData = false;
    },
  },
  components: {
    EntitiesTable,
    ReleaseNotesNew,
    ReleaseNotesEdit,
    LoadingModal,
    FormTextarea,
    Modal,
  },
});
</script>
